import React, { useState } from "react";
import styles from "./TechWorkshops.module.css";
import {
  FiCode, FiCpu, FiLayers, FiShield, FiDatabase, FiSmartphone,
  FiMail, FiPhone, FiArrowRight, FiCheck, FiUsers, FiBookOpen, FiAward,
  FiZap, FiCopy, FiCheckCircle
} from "react-icons/fi";

const topics = [
  {
    icon: <FiCode />,
    title: "Web Development",
    color: "#5540C7",
    duration: "2–3 days",
    description:
      "Full-stack web development from fundamentals to deployment. Covers HTML/CSS, JavaScript, React, Node.js, and REST APIs with hands-on projects.",
    highlights: ["HTML, CSS & JavaScript", "React & Component Design", "Node.js & Express", "REST APIs & Databases", "Deployment & CI/CD"],
  },
  {
    icon: <FiLayers />,
    title: "Blockchain & Web3",
    color: "#240BAD",
    duration: "2–3 days",
    description:
      "Deep dive into blockchain technology, smart contracts, and decentralized applications. Students build real DApps on Ethereum by the end.",
    highlights: ["Blockchain Fundamentals", "Ethereum & Solidity", "Smart Contract Development", "DApp Architecture", "NFTs & DeFi Concepts"],
  },
  {
    icon: <FiCpu />,
    title: "Artificial Intelligence & ML",
    color: "#8a75f6",
    duration: "2–3 days",
    description:
      "Introduction to AI/ML concepts with practical Python projects. Covers supervised learning, neural networks, and real-world AI applications.",
    highlights: ["Python for AI/ML", "Supervised & Unsupervised Learning", "Neural Networks & Deep Learning", "NLP Basics", "Building & Deploying ML Models"],
  },
  {
    icon: <FiShield />,
    title: "Cybersecurity",
    color: "#5540C7",
    duration: "1–2 days",
    description:
      "Fundamentals of ethical hacking and cybersecurity. Students learn to identify vulnerabilities, secure systems, and understand the threat landscape.",
    highlights: ["Security Fundamentals", "Network Security", "Web Application Security (OWASP)", "Ethical Hacking Basics", "CTF Challenges"],
  },
  {
    icon: <FiDatabase />,
    title: "Data Science & Analytics",
    color: "#240BAD",
    duration: "2 days",
    description:
      "Data collection, analysis, and visualization using Python and popular libraries. Students work on real datasets to extract meaningful insights.",
    highlights: ["Python (Pandas, NumPy)", "Data Visualization", "Statistical Analysis", "Exploratory Data Analysis", "Business Insights from Data"],
  },
  {
    icon: <FiSmartphone />,
    title: "Mobile App Development",
    color: "#8a75f6",
    duration: "2–3 days",
    description:
      "Cross-platform mobile app development using React Native. Students design and ship a working app for both Android and iOS.",
    highlights: ["React Native Basics", "UI/UX for Mobile", "State Management", "APIs & Authentication", "Publish to App Store / Play Store"],
  },
];

const plans = [
  {
    name: "Basic",
    price: "₹19,999",
    duration: "1-day workshop",
    color: "#5540C7",
    description: "Perfect for an introductory session or a single-topic awareness event.",
    features: [
      "1 topic of your choice",
      "Up to 60 students",
      "Workshop material included",
      "Q&A session",
      "Participation certificates",
    ],
    cta: "Get Quote",
    highlight: false,
  },
  {
    name: "Standard",
    price: "₹29,999",
    duration: "2-day workshop",
    color: "#240BAD",
    description: "Our most popular package — hands-on learning with a real project outcome.",
    features: [
      "1 topic (in-depth)",
      "Up to 100 students",
      "Workshop material included",
      "Hands-on project",
      "Q&A + mentoring session",
      "Participation certificates",
      "Post-workshop resource kit",
    ],
    cta: "Get Quote",
    highlight: true,
  },
  {
    name: "Premium",
    price: "₹59,999",
    duration: "3-day bootcamp",
    color: "#8a75f6",
    description: "A full bootcamp experience — multiple topics, team projects, and industry insights.",
    features: [
      "2–3 topics covered",
      "Unlimited students",
      "Full workshop material",
      "Team-based projects",
      "Industry expert sessions",
      "Q&A + career guidance",
      "Certificates of completion",
      "Dedicated post-event support",
    ],
    cta: "Get Quote",
    highlight: false,
  },
];

const whyUs = [
  {
    icon: <FiUsers />,
    title: "Industry Practitioners",
    desc: "Our instructors are working professionals with real-world experience, not just academics.",
  },
  {
    icon: <FiBookOpen />,
    title: "Hands-On Curriculum",
    desc: "Every workshop is built around practical projects — students leave with something they actually built.",
  },
  {
    icon: <FiAward />,
    title: "Recognised Certificates",
    desc: "Students receive certificates from Blockenzyme that they can showcase on their resumes and LinkedIn.",
  },
  {
    icon: <FiZap />,
    title: "Customisable Content",
    desc: "We tailor the curriculum to match your college's tech stack, semester syllabus, or placement focus.",
  },
];

const TechWorkshops = () => {
  const [copied, setCopied] = useState(null);

  const handleCopy = (e, text, key) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div className={styles.page}>

      {/* ─── HERO ─── */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroBadge}>
            <FiAward /> &nbsp; Tech Workshops for Colleges &amp; Universities
          </div>
          <h1 className={styles.heroTitle}>
            Bring Industry-Grade <span className={styles.highlight}>Tech Education</span> to Your Campus
          </h1>
          <p className={styles.heroSubtitle}>
            Blockenzyme organises immersive, hands-on tech workshops for engineering and science colleges. We cover the skills that top companies actually hire for — delivered by industry practitioners.
          </p>
          <div className={styles.heroBtns}>
            <a href="#contact" className={styles.primaryBtn}>
              Schedule a Workshop <FiArrowRight />
            </a>
            <a href="#topics" className={styles.secondaryBtn}>
              Explore Topics <FiArrowRight />
            </a>
          </div>
        </div>
      </section>

      {/* ─── STATS ─── */}
      <section className={styles.statsBar}>
        <div className={styles.statsInner}>
          <div className={styles.stat}><span className={styles.statNum}>500+</span><span className={styles.statLabel}>Students Trained</span></div>
          <div className={styles.statDivider} />
          <div className={styles.stat}><span className={styles.statNum}>10+</span><span className={styles.statLabel}>Colleges Partnered</span></div>
          <div className={styles.statDivider} />
          <div className={styles.stat}><span className={styles.statNum}>6</span><span className={styles.statLabel}>Workshop Topics</span></div>
          <div className={styles.statDivider} />
          <div className={styles.stat}><span className={styles.statNum}>4.9★</span><span className={styles.statLabel}>Average Rating</span></div>
        </div>
      </section>

      {/* ─── TOPICS ─── */}
      <section className={styles.section} id="topics">
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Workshop Topics</h2>
            <p className={styles.sectionSubtitle}>
              Choose from our curated list of in-demand tech domains — or combine multiple topics for a multi-day bootcamp.
            </p>
          </div>
          <div className={styles.topicsGrid}>
            {topics.map((topic, i) => (
              <div key={i} className={styles.topicCard}>
                <div className={styles.topicHeader}>
                  <span className={styles.topicIcon} style={{ color: topic.color, background: topic.color + "18" }}>
                    {topic.icon}
                  </span>
                  <span className={styles.topicDuration}>{topic.duration}</span>
                </div>
                <h3 className={styles.topicTitle}>{topic.title}</h3>
                <p className={styles.topicDesc}>{topic.description}</p>
                <ul className={styles.topicList}>
                  {topic.highlights.map((h, j) => (
                    <li key={j} className={styles.topicListItem}>
                      <FiCheck className={styles.checkIcon} style={{ color: topic.color }} />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHY US ─── */}
      <section className={styles.whySection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Why Colleges Choose Us</h2>
            <p className={styles.sectionSubtitle}>We don't just deliver lectures — we deliver outcomes.</p>
          </div>
          <div className={styles.whyGrid}>
            {whyUs.map((item, i) => (
              <div key={i} className={styles.whyCard}>
                <div className={styles.whyIcon}>{item.icon}</div>
                <h4 className={styles.whyTitle}>{item.title}</h4>
                <p className={styles.whyDesc}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section className={styles.section} id="pricing">
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Pricing Plans</h2>
            <p className={styles.sectionSubtitle}>
              Transparent, flexible pricing designed for educational institutions. All prices are per event.
            </p>
          </div>
          <div className={styles.plansGrid}>
            {plans.map((plan, i) => (
              <div
                key={i}
                className={`${styles.planCard} ${plan.highlight ? styles.planCardHighlight : ""}`}
              >
                {plan.highlight && (
                  <div className={styles.popularBadge}>Most Popular</div>
                )}
                <div className={styles.planName}>{plan.name}</div>
                <div className={styles.planPrice}>{plan.price}</div>
                <div className={styles.planDuration}>{plan.duration}</div>
                <p className={styles.planDesc}>{plan.description}</p>
                <ul className={styles.planFeatures}>
                  {plan.features.map((f, j) => (
                    <li key={j} className={styles.planFeatureItem}>
                      <FiCheck className={styles.checkIcon} style={{ color: plan.color }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <a href="#contact" className={`${styles.planBtn} ${plan.highlight ? styles.planBtnPrimary : styles.planBtnOutline}`}
                  style={plan.highlight ? {} : { borderColor: plan.color, color: plan.color }}>
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>
          <p className={styles.pricingNote}>
            * Prices are indicative. Final pricing depends on location, number of students, and customisation. Contact us for a custom quote.
          </p>
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <section className={styles.contactSection} id="contact">
        <div className={styles.contactInner}>
          <div className={styles.contactLeft}>
            <div className={styles.contactEyebrow}>Get in Touch</div>
            <h2 className={styles.contactTitle}>Ready to Host a Workshop at Your College?</h2>
            <p className={styles.contactSubtitle}>
              Reach out to us and we'll get back to you within 24 hours to discuss dates, topics, and pricing.
            </p>
            <div className={styles.contactCards}>
              <a href="mailto:swapnilkole7500@gmail.com" className={styles.contactCard}>
                <div className={styles.contactCardIcon}><FiMail /></div>
                <div className={styles.contactCardText}>
                  <div className={styles.contactCardLabel}>Email Us</div>
                  <div className={styles.contactCardValue}>swapnilkole7500@gmail.com</div>
                </div>
                <button
                  className={styles.copyBtn}
                  onClick={(e) => handleCopy(e, "workshops@blockenzyme.com", "email")}
                  title="Copy email"
                >
                  {copied === "email" ? <FiCheckCircle className={styles.copiedIcon} /> : <FiCopy />}
                </button>
              </a>
              <a href="tel:+918328217163" className={styles.contactCard}>
                <div className={styles.contactCardIcon}><FiPhone /></div>
                <div className={styles.contactCardText}>
                  <div className={styles.contactCardLabel}>Call / WhatsApp</div>
                  <div className={styles.contactCardValue}>+91 83282 17163</div>
                </div>
                <button
                  className={styles.copyBtn}
                  onClick={(e) => handleCopy(e, "+918328217163", "phone")}
                  title="Copy number"
                >
                  {copied === "phone" ? <FiCheckCircle className={styles.copiedIcon} /> : <FiCopy />}
                </button>
              </a>
            </div>
          </div>
          <div className={styles.contactRight}>
            <div className={styles.contactInfoBox}>
              <h4 className={styles.infoBoxTitle}>What happens next?</h4>
              <ol className={styles.infoBoxSteps}>
                <li><span className={styles.infoBoxNum}>1</span>You reach out via email or phone</li>
                <li><span className={styles.infoBoxNum}>2</span>We schedule a 15-min discovery call</li>
                <li><span className={styles.infoBoxNum}>3</span>We send you a custom proposal</li>
                <li><span className={styles.infoBoxNum}>4</span>Workshop confirmed &amp; dates locked!</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default TechWorkshops;
